import { STRIPE_PRODUCT_IDS } from '~/vendor/cx-web/repub-config';

import type { SupportedRepubName } from './types';

/**
 * Replaces the price card's term-derived copy with `label` keys named `${labelKeyPrefix}_<FIELD>`: TERM, PRICE and
 * CTA always render; BADGE (which also features the card), TAGLINE and BILLING_NOTE render only when translated.
 * PRICE wraps the amount in `<amount>` to style it apart, e.g. `<amount>€1</amount> / per 14 gg`.
 * `order` is the mobile order, `desktopOrder` the side-by-side order.
 */
export type PriceCardDisplayConfig = {
  desktopOrder: number;
  labelKeyPrefix: string;
  order: number;
};

export type MetaPixelProductConfig = {
  billingModel: string;
  productType: 'paid_subscription' | 'free_trial';
  trialDays?: number;
};

export type SubscriptionProductConfig = {
  display?: PriceCardDisplayConfig;
  externalUrl?: Partial<Record<'PROD' | 'NON_PROD', string>>;
  isDefaultWhenSingleOption: boolean;
  metaPixel?: MetaPixelProductConfig;
  name: 'WhoScored+' | 'Premium' | 'Essential';
  term: 'annually' | 'monthly';
};

export type SubscriptionConfig = {
  essentialFeatures?: { description?: string; isDisabled?: boolean; title: string }[];
  premiumFeatures: { description?: string; title: string }[];
  products: Record<string, SubscriptionProductConfig>;
};

export const SUBSCRIPTION_CONFIGS = {
  WS: {
    premiumFeatures: [
      { title: 'AI-powered projections' },
      { title: 'AI-powered probabilities' },
      { title: 'Easy Visualised Trends' },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.WHOSCORED_PLUS_MONTHLY]: {
        name: 'WhoScored+',
        term: 'monthly',
        isDefaultWhenSingleOption: true,
      },
    },
  },
  US: {
    premiumFeatures: [
      { title: 'Positive Expected Value Bets' },
      { title: 'AI Probabilities for each bet' },
      { title: 'Public Betting Splits' },
      { title: 'Trends and insights for Each Bet' },
      { title: 'Custom Betslip' },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY]: {
        name: 'Premium',
        term: 'annually',
        isDefaultWhenSingleOption: false,
      },
      [STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY]: { name: 'Premium', term: 'monthly', isDefaultWhenSingleOption: true },
    },
  },
  OC: {
    essentialFeatures: [
      {
        title: 'Unlimited Grids',
        description: 'Unlock unlimited comparison grids for over 500 sporting events daily',
      },
      {
        title: 'Market Movers',
        description: 'Unlock unlimited access to the top steamers and drifters daily',
      },
      {
        title: 'Reduced Ads',
        description: 'Never see banner advertising or popups again',
      },
      {
        title: 'Price History',
        description: 'Get unlimited access to price history across 25+ bookmakers',
      },
      {
        title: 'Premium Content',
        isDisabled: true,
      },
      {
        title: '60% off RacingTV',
        isDisabled: true,
      },
      {
        title: 'Value Bets',
        isDisabled: true,
      },
      {
        title: 'Trends',
        isDisabled: true,
      },
      {
        title: 'Public Betting Splits',
        isDisabled: true,
      },
    ],
    premiumFeatures: [
      {
        title: 'Unlimited Grids',
        description: 'Unlock unlimited comparison grids for over 500 sporting events daily',
      },
      {
        title: 'AI Probabilities on Grids',
        description: 'Visibility of our cutting edge AI probabilities on supported sporting events and markets',
      },
      {
        title: 'Market Movers',
        description: 'Unlock unlimited access to the top steamers and drifters daily',
      },
      {
        title: 'Reduced Ads',
        description: 'Never see banner advertising or popups again',
      },
      {
        title: 'Price History',
        description: 'Get unlimited access to price history across 25+ bookmakers',
      },
      {
        title: 'Premium Content',
        description: 'Unlock exclusive access to Andy Holding, Ginger Joe, Liam Firkin & more',
      },
      {
        title: '60% off RacingTV',
        description:
          'Get an exclusive 60% off RacingTV subscription at £11.99 per month for 12 months. Available to new and returning RacingTV customers',
      },
      {
        title: 'Value Bets',
        description: 'Get access to our Value Bets tool powered by cutting edge AI',
      },
      {
        title: 'Trends',
        description: 'Identify the best betting opportunities with our AI powered Trends',
      },
      {
        title: 'Public Betting Splits',
        description: 'Get access to where the smart money is going',
      },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.ESSENTIAL_MONTHLY]: {
        name: 'Essential',
        term: 'monthly',
        isDefaultWhenSingleOption: false,
      },
      [STRIPE_PRODUCT_IDS.ESSENTIAL_ANNUALLY]: {
        name: 'Essential',
        term: 'annually',
        isDefaultWhenSingleOption: false,
      },
      [STRIPE_PRODUCT_IDS.PREMIUM_ANNUALLY]: { name: 'Premium', term: 'annually', isDefaultWhenSingleOption: false },
      [STRIPE_PRODUCT_IDS.PREMIUM_MONTHLY]: { name: 'Premium', term: 'monthly', isDefaultWhenSingleOption: true },
    },
  },
  GZ: {
    premiumFeatures: [
      { title: '+100 pronostici e quote con EV positivo al giorno' },
      { title: 'Calcolo della Probabilità AI per ogni scommessa' },
      { title: 'Tutti gli operatori comparati in tempo reale' },
      { title: 'Studi e approfondimenti per ogni scommessa' },
    ],
    products: {
      [STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_OFFER_1]: {
        display: {
          desktopOrder: 1,
          labelKeyPrefix: 'CX_PLUS_GZ_OFFER_1',
          order: 2,
        },
        externalUrl: {
          NON_PROD:
            'https://dev.abbonamenti.gazzetta.it/login/?OfferId=OFF-00138537_1&returnUrl=https://dv1-fpsm.gazzetta.it',
          PROD: 'https://abbonamenti.gazzetta.it/login/?OfferId=OFF-00138537_1&returnUrl=https://aipredictor.gazzetta.it',
        },
        isDefaultWhenSingleOption: true,
        metaPixel: {
          billingModel: 'monthly_after_trial',
          productType: 'paid_subscription',
          trialDays: 14,
        },
        name: 'Premium',
        term: 'monthly',
      },
      [STRIPE_PRODUCT_IDS.GAZZETTA_PLUS_OFFER_2]: {
        display: {
          desktopOrder: 2,
          labelKeyPrefix: 'CX_PLUS_GZ_OFFER_2',
          order: 1,
        },
        externalUrl: {
          NON_PROD:
            'https://dev.abbonamenti.gazzetta.it/login/?OfferId=OFF-00138527_1&returnUrl=https://dv1-fpsm.gazzetta.it',
          PROD: 'https://abbonamenti.gazzetta.it/login/?OfferId=OFF-00138527_1&returnUrl=https://aipredictor.gazzetta.it',
        },
        isDefaultWhenSingleOption: false,
        metaPixel: {
          billingModel: 'annual',
          productType: 'paid_subscription',
        },
        name: 'Premium',
        term: 'annually',
      },
    },
  },
} satisfies Record<SupportedRepubName, SubscriptionConfig>;

export function getSubscriptionProductConfig(
  repub: SupportedRepubName,
  productId: string,
): SubscriptionProductConfig | undefined {
  const products: Record<string, SubscriptionProductConfig> = SUBSCRIPTION_CONFIGS[repub].products;
  return products[productId];
}
